import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { getSupabaseSession } from "@/app/_module/services/auth";
import AgencyTokenManager from "@/app/_module/services/agencyTokenManager";

// ── Shell-aware routing ──────────────────────────────────────────────────
// Inside the native app every screen lives under /m (src/app/m/forum/**
// wraps these same components 1:1). Every in-module link goes through here
// so desktop and mobile can't drift: forumPath("/t/slug") → "/forum/t/slug"
// on the web, "/m/forum/t/slug" in the shell.
export function shellPrefix(): string {
  return typeof window !== "undefined" && window.location.pathname.startsWith("/m") ? "/m" : "";
}
export function forumPath(sub = ""): string {
  return `${shellPrefix()}/forum${sub}`;
}

// ─── Types (mirror backend serializers) ─────────────────────────────────────

export interface ForumAuthor {
  id: string;
  name: string;
  // Admin-authored content is published as "InsuraCentral" with a Staff mark.
  isStaff?: boolean;
  // Public profile handle on forum.insuracentral.com (/u/<handle>); null until first post.
  handle?: string | null;
  avatarUrl: string | null;
  karma: number;
  // "Verified on Licensedproducer" — public directory slug, null when unlinked
  lpSlug?: string | null;
}

export interface ForumCategory {
  id: string;
  slug: string;
  name: string;
  description: string;
  icon: string | null;
  isAdminPostOnly: boolean;
  isOfficial: boolean;
  postCount: number;
}

export interface ForumPostSummary {
  id: string;
  slug: string;
  title: string;
  bodyPreview: string;
  imageUrl: string | null;
  category: { id: string; slug: string; name: string; icon: string | null };
  author: ForumAuthor;
  score: number;
  upvotes: number;
  downvotes: number;
  commentCount: number;
  viewCount: number;
  isPinned: boolean;
  isLocked: boolean;
  isEdited: boolean;
  // Page-1 "fresh slot": a new thread with almost no replies, given its look.
  isFresh?: boolean;
  myVote: 1 | -1 | null;
  createdAt: string;
  lastActivityAt: string;
}

export interface ForumPostDetail extends Omit<ForumPostSummary, "bodyPreview" | "imageUrl" | "author"> {
  body: string;
  images: { url: string; width: number | null; height: number | null }[];
  isOwn: boolean;
  // Reddit-style tombstone: author null, body "[deleted]"/"[removed]"
  author: ForumAuthor | null;
  isDeleted?: boolean;
}

export interface ForumComment {
  id: string;
  postId: string;
  parentCommentId: string | null;
  rootCommentId: string | null;
  depth: number;
  body: string;
  isRemoved: boolean;
  author: ForumAuthor | null;
  isOwn: boolean;
  score: number;
  upvotes: number;
  downvotes: number;
  replyCount: number;
  isEdited: boolean;
  myVote: 1 | -1 | null;
  createdAt: string;
}

export interface ForumFeedResponse {
  posts: ForumPostSummary[];
  pinned: ForumPostSummary[];
  hasMore: boolean;
  nextCursor: string | null;
}

export interface ForumCommentsResponse {
  comments: ForumComment[];
  totalCount: number;
  capped: boolean;
  hasMore: boolean;
  nextCursor: string | null;
}

export interface ForumEligibility {
  eligible: boolean;
  canWrite: boolean;
  reason:
    | null
    | "FORUM_NOT_AVAILABLE"
    | "FORUM_DISABLED_BY_AGENCY"
    | "FORUM_OPT_IN_REQUIRED"
    | "FORUM_SUBSCRIPTION_REQUIRED"
    | "FORUM_DEMO_READ_ONLY"
    | "FORUM_BANNED";
  isModerator: boolean;
  // Who can flip the switch: agency owners ("agency") and solo accounts ("user").
  canToggleAgency: boolean;
  agencyToggleEnabled: boolean;
  toggleScope: "agency" | "user" | "none";
  banned?: { until: string | null; reason: string | null };
  stats: { postKarma: number; commentKarma: number; karma: number; postCount: number; commentCount: number };
  viewer: { id: string; name: string };
}

export interface ForumUserProfile {
  id: string;
  name: string;
  isStaff?: boolean;
  // Public profile handle on forum.insuracentral.com (/u/<handle>); null until first post.
  handle?: string | null;
  avatarUrl: string | null;
  lpSlug: string | null;
  postKarma: number;
  commentKarma: number;
  karma: number;
  postCount: number;
  commentCount: number;
  joinedAt: string;
  recentPosts: { slug: string; title: string; score: number; commentCount: number; category: string; createdAt: string }[];
}

// "The Board" — 30-day floor numbers + thread of the month.
export interface ForumBoard {
  window: "30d" | "all";
  threads: number;
  replies: number;
  wins: number;
  caseHelp: number;
  agents: number;
  featured: {
    slug: string;
    title: string;
    score: number;
    commentCount: number;
    category: { name: string; slug: string };
    author: { name: string; isStaff?: boolean };
    createdAt: string;
  } | null;
}

export interface ForumSearchHit extends ForumPostSummary {
  snippet: string;
  matchedIn: "title" | "body" | "replies" | "author" | "category" | "close";
  replyHits: number;
}
export interface ForumSearchResponse {
  query: string;
  posts: ForumSearchHit[];
  hasMore: boolean;
  page: number;
  terms: string[];
}

export type FeedSort = "hot" | "top" | "new";
export type CommentSort = "top" | "new";

// Public profile base for the Licensedproducer badge click-through.
export const LP_PROFILE_BASE =
  (process.env.NEXT_PUBLIC_LICENSEDPRODUCER_URL || "https://licensedproducer.com").replace(/\/+$/, "");
export const lpProfileUrl = (slug: string) => `${LP_PROFILE_BASE}/agents/${slug}`;

// Structured error — carries the backend's machine-readable code so the UI can
// route FORUM_SUBSCRIPTION_REQUIRED → SubscriptionGateModal etc.
export class ForumApiError extends Error {
  status: number;
  code: string | null;
  constructor(status: number, code: string | null, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function authHeaders(json = true) {
  const session = await getSupabaseSession();
  const agencyId = AgencyTokenManager.getCurrentAgencyId();
  return {
    Authorization: `Bearer ${session?.access_token}`,
    ...(json ? { "Content-Type": "application/json" } : {}),
    ...(agencyId && { "x-agency-id": agencyId }),
  } as Record<string, string>;
}

async function fetchJson<T = any>(url: string, options?: RequestInit): Promise<T> {
  const headers = await authHeaders(!(options?.body instanceof FormData));
  const res = await fetch(getApiUrl(url), { ...options, headers: { ...headers, ...options?.headers } });
  const text = await res.text();
  let json: any = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    /* non-JSON error body */
  }
  if (!res.ok) {
    const code = json?.error || json?.data?.error || null;
    const message = json?.message || json?.data?.message || `Request failed (${res.status})`;
    throw new ForumApiError(res.status, code, message);
  }
  // Backend envelope: { statusCode, message, data }
  const outer = json?.data;
  if (outer && typeof outer === "object" && "data" in outer && Object.keys(outer).length <= 3) {
    return outer.data as T;
  }
  return (outer ?? json) as T;
}

const qs = (params: Record<string, string | number | undefined | null>) => {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join("&")}` : "";
};

export const forumService = {
  getEligibility: (): Promise<ForumEligibility> => fetchJson(endpoint.forum.eligibility),
  getBoard: (): Promise<ForumBoard> => fetchJson(endpoint.forum.board),
  search: (q: string, page = 0): Promise<ForumSearchResponse> =>
    fetchJson(`${endpoint.forum.search}${qs({ q, page })}`),

  getCategories: (): Promise<ForumCategory[]> => fetchJson(endpoint.forum.categories),

  createCategory: (input: { name: string; description?: string }): Promise<{ id: string; slug: string; name: string }> =>
    fetchJson(endpoint.forum.categories, { method: "POST", body: JSON.stringify(input) }),

  getFeed: (opts: { sort?: FeedSort; category?: string | null; cursor?: string | null; t?: string }): Promise<ForumFeedResponse> =>
    fetchJson(`${endpoint.forum.posts}${qs({ sort: opts.sort, category: opts.category, cursor: opts.cursor, t: opts.t, limit: 25 })}`),

  getLatest: (): Promise<{ latest: string | null }> => fetchJson(endpoint.forum.latest),

  getPost: (idOrSlug: string): Promise<ForumPostDetail> => fetchJson(endpoint.forum.post(idOrSlug)),

  // View beacon: one counted view per account per post per 12h (server-side).
  registerView: (postId: string): Promise<{ counted: boolean }> =>
    fetchJson(`${endpoint.forum.post(postId)}/view`, { method: "POST" }),

  createPost: (input: { categoryId: string; title: string; body?: string; imagePaths?: string[] }): Promise<{ id: string; slug: string }> =>
    fetchJson(endpoint.forum.posts, { method: "POST", body: JSON.stringify(input) }),

  updatePost: (id: string, body: string): Promise<{ ok: boolean }> =>
    fetchJson(endpoint.forum.post(id), { method: "PATCH", body: JSON.stringify({ body }) }),

  deletePost: (id: string): Promise<{ ok: boolean }> =>
    fetchJson(endpoint.forum.post(id), { method: "DELETE" }),

  getComments: (postId: string, opts: { sort?: CommentSort; cursor?: string | null; rootId?: string | null } = {}): Promise<ForumCommentsResponse> =>
    fetchJson(`${endpoint.forum.comments(postId)}${qs({ sort: opts.sort, cursor: opts.cursor, rootId: opts.rootId })}`),

  createComment: (postId: string, input: { body: string; parentCommentId?: string | null }): Promise<{ id: string }> =>
    fetchJson(endpoint.forum.comments(postId), { method: "POST", body: JSON.stringify(input) }),

  updateComment: (id: string, body: string): Promise<{ ok: boolean }> =>
    fetchJson(endpoint.forum.comment(id), { method: "PATCH", body: JSON.stringify({ body }) }),

  deleteComment: (id: string): Promise<{ ok: boolean }> =>
    fetchJson(endpoint.forum.comment(id), { method: "DELETE" }),

  vote: (input: { targetType: "post" | "comment"; targetId: string; value: 1 | -1 | 0 }): Promise<{ targetId: string; score: number; upvotes: number; downvotes: number; myVote: 1 | -1 | null }> =>
    fetchJson(endpoint.forum.votes, { method: "PUT", body: JSON.stringify(input) }),

  report: (input: { targetType: "post" | "comment"; targetId: string; reason: string; details?: string }): Promise<{ ok: boolean; alreadyReported?: boolean }> =>
    fetchJson(endpoint.forum.reports, { method: "POST", body: JSON.stringify(input) }),

  uploadImage: async (file: File): Promise<{ path: string; url: string; width: number | null; height: number | null }> => {
    const form = new FormData();
    form.append("file", file);
    return fetchJson(endpoint.forum.media, { method: "POST", body: form });
  },

  getUserProfile: (userId: string): Promise<ForumUserProfile> => fetchJson(endpoint.forum.user(userId)),

  getAgencySettings: (): Promise<{ hasAgency: boolean; enabled: boolean; canToggle: boolean; scope?: "agency" | "user" }> =>
    fetchJson(endpoint.forum.agencySettings),

  setAgencySettings: (enabled: boolean): Promise<{ enabled: boolean }> =>
    fetchJson(endpoint.forum.agencySettings, { method: "PUT", body: JSON.stringify({ enabled }) }),

  // Moderation (platform admin)
  mod: {
    getStats: (): Promise<{ pending_reports: number; blocked_24h: number; flagged_7d: number; posts: number; comments: number; banned_users: number; weekly_posters: number }> =>
      fetchJson("v1/forum/mod/stats"),
    getReports: (status = "pending"): Promise<any[]> => fetchJson(`${endpoint.forum.mod.reports}?status=${status}&limit=100`),
    // Automated-rail audit trail (PII firewall / promotion detector / AI screener).
    getScreenings: (decision?: "block" | "flag", limit = 50): Promise<any[]> =>
      fetchJson(`${endpoint.forum.mod.screenings}?limit=${limit}${decision ? `&decision=${decision}` : ""}`),
    reviewReport: (id: string, status: "actioned" | "dismissed") =>
      fetchJson(endpoint.forum.mod.report(id), { method: "PATCH", body: JSON.stringify({ status }) }),
    postAction: (id: string, action: "pin" | "unpin" | "lock" | "unlock" | "restore") =>
      fetchJson(endpoint.forum.mod.postAction(id, action), { method: "POST" }),
    removePost: (id: string, reason: string) =>
      fetchJson(endpoint.forum.mod.postAction(id, "remove"), { method: "POST", body: JSON.stringify({ reason }) }),
    removeComment: (id: string, reason: string) =>
      fetchJson(endpoint.forum.mod.commentAction(id, "remove"), { method: "POST", body: JSON.stringify({ reason }) }),
    restoreComment: (id: string) =>
      fetchJson(endpoint.forum.mod.commentAction(id, "restore"), { method: "POST" }),
    banUser: (userId: string, input: { until?: string | null; reason: string; purgeRecent?: boolean }) =>
      fetchJson(endpoint.forum.mod.ban(userId), { method: "POST", body: JSON.stringify(input) }),
  },
};

// Compact score formatting: 999 → 999, 12345 → 12.3k, 1200000 → 1.2m
export function formatScore(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}m`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  return String(n);
}

export function timeAgo(iso: string): string {
  const secs = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo`;
  return `${Math.floor(months / 12)}y`;
}
